import React from 'react';
import { css } from '@emotion/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { BirdiTheme, blocks, fonts } from '@birdi/theme';
import type { MemberWorkspaceSummary } from '@birdi/js-sdk/src/public/organisation';
import WorkspacesSVG from '@birdi/icons/menu-workspaces.svg';
import ChevronDownSVG from '@birdi/icons/chevron-down.svg';
import { workspaceIconStyle } from '../common/side-menu/SideNavigationDashboard';

export interface WorkspaceFilterProps {
  workspaces: MemberWorkspaceSummary[];
  /** null = every workspace the user belongs to. */
  value: string | null;
  onChange: (workspaceId: string | null) => void;
}

const ALL_LABEL = 'All workspaces';

/**
 * Workspace filter above the results (PRD): narrows search, browse and the
 * globe pins to one of the user's workspaces. Same Radix dropdown, trigger
 * and row treatment as the dashboard side-menu workspace switcher
 * (`user-workspaces.tsx`), so it reads as the same control — but it filters
 * search rather than switching the session. Hidden when the user only
 * belongs to one workspace, since there is nothing to choose.
 */
export const WorkspaceFilter = ({
  workspaces,
  value,
  onChange,
}: WorkspaceFilterProps) => {
  if (workspaces.length < 2) return null;

  const current = workspaces.find((ws) => ws.id === value) ?? null;

  const icon = (ws: MemberWorkspaceSummary | null) =>
    ws?.menuIcon ? (
      <img src={ws.menuIcon} alt="" css={menuIconCss} />
    ) : (
      <span css={workspaceIconStyle}>
        <WorkspacesSVG />
      </span>
    );

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          css={triggerCss}
          data-testid="global-search-workspace-filter"
          aria-label="Filter by workspace"
        >
          <span css={triggerMainCss}>
            {icon(current)}
            <span css={nameCss}>{current ? current.name : ALL_LABEL}</span>
          </span>
          <span css={chevronCss}>
            <ChevronDownSVG />
          </span>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={6}
          css={[blocks.radixDropdownAnimation, contentCss]}
        >
          <h3 css={headingCss}>Search in</h3>
          <hr css={ruleCss} />
          <DropdownMenu.Item asChild>
            <button
              type="button"
              css={itemCss(value === null)}
              onClick={() => onChange(null)}
              data-testid="global-search-workspace-option-all"
            >
              <span css={itemMainCss}>
                <span css={workspaceIconStyle}>
                  <WorkspacesSVG />
                </span>
                <span css={itemNameCss}>{ALL_LABEL}</span>
              </span>
            </button>
          </DropdownMenu.Item>
          {workspaces.map((ws) => (
            <DropdownMenu.Item key={ws.id} asChild>
              <button
                type="button"
                css={itemCss(ws.id === value)}
                onClick={() => onChange(ws.id)}
                data-testid={`global-search-workspace-option-${ws.id}`}
              >
                <span css={itemMainCss}>
                  {icon(ws)}
                  <span css={itemNameCss}>{ws.name}</span>
                </span>
              </button>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

/* Trigger: the dashboard switcher's row, as a bordered control above the
   tiles. */
const triggerCss = (theme: BirdiTheme) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75em;
  width: 100%;
  flex-shrink: 0;
  margin-bottom: 0.75rem;
  padding: 0.5em 0.75em;
  border: 1px solid ${theme.dove};
  border-radius: 5px;
  background: ${theme.mono0};
  color: ${theme.midnight};
  font-family: ${fonts.body};
  font-size: 0.875rem;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: ${theme.mono20};
  }
  &:focus-visible {
    outline: 2px solid ${theme.deepOcean};
    outline-offset: 1px;
  }
`;

const triggerMainCss = css`
  display: flex;
  align-items: center;
  gap: 0.75em;
  min-width: 0;
`;

const nameCss = css`
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const chevronCss = (theme: BirdiTheme) => css`
  display: flex;
  flex-shrink: 0;
  color: ${theme.mono70};
  svg {
    width: 1em;
    height: 1em;
  }
`;

const menuIconCss = css`
  width: 1.5em;
  height: 1.5em;
  border-radius: 5px;
  object-fit: cover;
  flex-shrink: 0;
`;

/* Same panel as the dashboard switcher. */
const contentCss = (theme: BirdiTheme) => css`
  display: flex;
  flex-direction: column;
  width: 320px;
  padding: 0 1em;
  color: ${theme.midnight};
  background: ${theme.mono0};
  box-shadow: 0 4px 8px ${theme.mono40};
  border-radius: 5px;
  overflow: hidden auto;
  max-height: calc(100vh - 10em);
  z-index: 2147483602; /* above the search panel, which sits above Intercom */
  font-family: ${fonts.body};
`;

const headingCss = css`
  margin: 0.5em;
  padding: 0;
  font-size: 1rem;
`;

const ruleCss = css`
  width: 100%;
  margin: 0.5em 0;
`;

const itemCss = (active: boolean) => (theme: BirdiTheme) => css`
  width: 100%;
  margin-bottom: 0.5em;
  padding: 0.5em;
  border: none;
  border-radius: 0.25em;
  white-space: nowrap;
  text-align: left;
  background: ${active ? theme.lightOcean : theme.mono0};
  cursor: pointer;

  &:hover {
    background: ${active ? theme.lightOcean : theme.mono20};
  }
  &:focus {
    outline: none;
  }
`;

const itemMainCss = css`
  display: flex;
  align-items: center;
`;

const itemNameCss = css`
  font-weight: bold;
  margin-left: 0.75em;
  max-width: 12em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
`;
